import { useCallback } from 'react';

import { useDeleteTeamMutation, useListTeamswithMembersQuery } from '@/@generated/codegen/API';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PageLoading } from '@/ui/PageLoading';
import { usePrevious } from '@/utils/helpers/react.helper';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import AdminTeamListView from './AdminTeamListView';
import { AdminTeamListItemType } from './columns';
import { useAdminTeamListQueryParams } from './hooks/useAdminTeamListQueryParams';

const PAGE_SIZE = 15;
export const teamTablePrefix = 'teams';

export default function AdminTeamListClient() {
  const { paginationArgs, orderByArgs, where } = useAdminTeamListQueryParams(
    PAGE_SIZE,
    teamTablePrefix
  );
  const teams = useListTeamswithMembersQuery({
    variables: {
      paginationArgs,
      orderByArgs,
      where,
    },
  });
  const previousData = usePrevious(teams.data);
  const pagination = usePaginationState(PAGE_SIZE, teamTablePrefix);
  const [deleteTeamMutation] = useDeleteTeamMutation();

  useEntitySubscription('Team', {
    onCreate: {
      variables: {
        where,
      },
      onData: ({ data }) => {
        const obj = data.data?.onCreateTeam;
        if (!obj) {
          captureSentry({
            title: 'AdminTeamListClient | onData | onCreateTeam | obj is undefined',
            detail: {},
          });
          return;
        }
        void teams.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: ({ data }) => {
        const obj = data.data?.onDeleteTeam;
        if (!obj) {
          captureSentry({
            title: 'AdminTeamListClient | onData | onDeleteTeam | obj is undefined',
            detail: {},
          });
          return;
        }
        void teams.refetch();
      },
    },
  });

  const handleDeleteTeam = useCallback(
    (row: AdminTeamListItemType) => {
      void deleteTeamMutation({
        variables: {
          where: {
            id: row.id,
          },
        },
      });
    },
    [deleteTeamMutation]
  );

  if (!previousData && !teams.data) {
    return <PageLoading />;
  }

  return (
    <AdminTeamListView
      data={teams.data || previousData!}
      pagination={pagination}
      handleDeleteTeam={handleDeleteTeam}
      tablePrefix={teamTablePrefix}
    />
  );
}
